import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  coachSessionsEndpoints,
  ICreateCoachSessionBody,
  SessionObjective,
} from '../../../endpoints/coachSessions';
import { microcycleTemplatesEndpoints } from '../../../endpoints/microcycleTemplates';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { OBJECTIVE_LABELS } from '../../../pages/CoachSessionWeekView/useCoachSessionWeekView';
import { getSportCapabilities } from '@leopart/sport/sport-capabilities';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomDatePicker from '../../../shared/CustomDatePicker/CustomDatePicker';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import Typhography from '../../../shared/Typhography/Typhography';
import toast from 'react-hot-toast';
import styles from './NewFieldSessionModal.module.scss';

const OBJECTIVES: SessionObjective[] = [
  'tactical',
  'physical',
  'technical',
  'recovery',
  'match_prep',
  'mixed',
];

const toDate = (iso: string): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toIsoDate = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toTimeDate = (hhmm: string): Date | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

const toHhmm = (d: Date | null): string => {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type Recipient =
  | { type: 'team'; teamId: string }
  | { type: 'athletes'; athleteIds: string[] }
  | null;

/**
 * Bug 69f9ae9d — flow di creazione "Nuova Scheda Campo" allineato a quello
 * di Dettaglio Team > Programmazione Campo > "Nuova sessione" (CreateSessionModal),
 * con un pre-step iniziale per la scelta del destinatario (squadra vs atleti
 * singoli/multipli) — la sezione principale Programmazione non ha contesto
 * team/atleta.
 *
 * Submit → POST /coach-sessions con teamId o athleteIds[] → naviga alla
 * edit page della session creata.
 */
const NewFieldSessionModal: FC<Props> = ({ isOpen, onClose }) => {
  const api = useEndpoints();
  const navigate = useNavigate();

  const [step, setStep] = useState<0 | 1>(0);
  const [recipient, setRecipient] = useState<Recipient>(null);

  // Form state (step 1 — identico a CreateSessionModal flow B)
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(() => toIsoDate(new Date()));
  const [startTime, setStartTime] = useState('');
  const [objective, setObjective] = useState<SessionObjective>('mixed');
  const [keepersWorkSeparate, setKeepersWorkSeparate] = useState(false);
  const [templateSource, setTemplateSource] = useState<'empty' | 'leopart' | 'personal'>('empty');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Step 0 helpers
  const [recipientType, setRecipientType] = useState<'team' | 'athletes' | null>(null);
  const [pickedTeamId, setPickedTeamId] = useState('');
  const [pickedAthleteIds, setPickedAthleteIds] = useState<string[]>([]);

  // Lista team disponibili
  const teamsQ = useQuery({
    queryKey: ['teams-for-new-field-session'],
    queryFn: () => api.getTeams({ limit: 200, populate: 'spaceId' }).then((r) => r.data.docs ?? []),
    enabled: isOpen && recipientType === 'team',
    staleTime: 5 * 60 * 1000,
  });

  // Lista atleti (da team del director, semplice — l'utente sceglie liberamente)
  const athletesQ = useQuery({
    queryKey: ['athletes-for-new-field-session'],
    queryFn: () =>
      api
        .getUsers({ type: 'athlete', limit: 500 })
        .then((r) => r.data.docs ?? []),
    enabled: isOpen && recipientType === 'athletes',
    staleTime: 5 * 60 * 1000,
  });

  // Sport derivato dal team selezionato (per goalkeepers + microcycle templates filter)
  const sport = useMemo(() => {
    if (recipient?.type !== 'team') return undefined;
    const t = (teamsQ.data ?? []).find((x: any) => x._id === recipient.teamId);
    return (t?.spaceId?.type ?? undefined) as string | undefined;
  }, [recipient, teamsQ.data]);

  const { data: templates = [] } = useQuery({
    queryKey: ['microcycleTemplates-newField', templateSource, sport],
    queryFn: () =>
      microcycleTemplatesEndpoints
        .list({
          scope: templateSource === 'leopart' ? 'SYSTEM' : 'PERSONAL',
          sport,
        })
        .then((r) => r.data),
    enabled: isOpen && templateSource !== 'empty' && step === 1,
  });

  const createMut = useMutation({
    mutationFn: (body: ICreateCoachSessionBody) =>
      coachSessionsEndpoints.create(body).then((r) => r.data),
    onSuccess: (created) => {
      toast.success('Sessione Campo creata');
      onClose();
      // Naviga alla edit page della coach-session creata
      const teamPath = created.teamId
        ? `/dashboard/teams/${created.teamId}/sessions/${created._id}/edit`
        : `/dashboard/coach-sessions/${created._id}/edit`;
      navigate(teamPath);
    },
    onError: (err: any) => {
      const apiMsg = err?.response?.data?.message;
      const msg = Array.isArray(apiMsg)
        ? apiMsg.join(', ')
        : apiMsg || err?.message || 'Errore creazione sessione';
      toast.error(msg);
    },
  });

  const handleStep0Continue = () => {
    if (recipientType === 'team' && pickedTeamId) {
      setRecipient({ type: 'team', teamId: pickedTeamId });
      setStep(1);
    } else if (recipientType === 'athletes' && pickedAthleteIds.length > 0) {
      setRecipient({ type: 'athletes', athleteIds: pickedAthleteIds });
      setStep(1);
    }
  };

  const handleSubmit = () => {
    if (!recipient) return;
    if (!title.trim()) return;
    const body: ICreateCoachSessionBody = {
      title: title.trim(),
      scheduledDate: date,
      startTime: startTime || undefined,
      objective,
      status: 'draft',
      keepersWorkSeparate,
      ...(selectedTemplateId ? { templateId: selectedTemplateId } : {}),
      ...(recipient.type === 'team'
        ? { teamId: recipient.teamId }
        : { athleteIds: recipient.athleteIds }),
    };
    createMut.mutate(body);
  };

  const handleClose = () => {
    setStep(0);
    setRecipient(null);
    setRecipientType(null);
    setPickedTeamId('');
    setPickedAthleteIds([]);
    setTitle('');
    setDate(toIsoDate(new Date()));
    setStartTime('');
    setObjective('mixed');
    setKeepersWorkSeparate(false);
    setTemplateSource('empty');
    setSelectedTemplateId('');
    onClose();
  };

  if (!isOpen) return null;

  const Header = (
    <div className={styles.headerWrap}>
      <div className={styles.headerColorBar} />
      <div className={styles.headerContent}>
        <Typhography type="h3" size="bold-16">
          Nuova sessione Campo
        </Typhography>
        <Typhography type="p" size="medium-12">
          {step === 0
            ? 'Step 1 di 2 — Scegli il destinatario della sessione'
            : 'Step 2 di 2 — Definisci titolo, data, obiettivo e template di partenza'}
        </Typhography>
      </div>
    </div>
  );

  const step0Valid =
    (recipientType === 'team' && !!pickedTeamId) ||
    (recipientType === 'athletes' && pickedAthleteIds.length > 0);

  const Footer = (
    <div className={styles.footerWrap}>
      {step === 0 ? (
        <>
          <CustomButton color="outline" onClick={handleClose}>
            Annulla
          </CustomButton>
          <CustomButton
            color="secondary"
            disabled={!step0Valid}
            onClick={handleStep0Continue}
          >
            Avanti
          </CustomButton>
        </>
      ) : (
        <>
          <CustomButton color="outline" onClick={() => setStep(0)}>
            Indietro
          </CustomButton>
          <CustomButton
            color="secondary"
            disabled={!title.trim() || createMut.isPending}
            isLoading={createMut.isPending}
            onClick={handleSubmit}
          >
            Crea sessione
          </CustomButton>
        </>
      )}
    </div>
  );

  return (
    <GenericModalV2
      isOpen
      onClose={handleClose}
      size="md"
      Header={Header}
      Footer={Footer}
    >
      <div className={styles.body}>
        {step === 0 && (
          <div className={styles.bodyInner}>
            <div className={styles.field}>
              <label className={styles.label}>Tipo destinatario</label>
              <div className={styles.recipientPickerRow}>
                <button
                  type="button"
                  className={`${styles.recipientBtn} ${recipientType === 'team' ? styles.recipientBtnActive : ''}`}
                  onClick={() => setRecipientType('team')}
                >
                  <strong>Squadra</strong>
                  <span className={styles.recipientHint}>
                    Sessione a livello team con roster già caricato
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.recipientBtn} ${recipientType === 'athletes' ? styles.recipientBtnActive : ''}`}
                  onClick={() => setRecipientType('athletes')}
                >
                  <strong>Atleti singoli/multipli</strong>
                  <span className={styles.recipientHint}>
                    Sessione individuale o per sottoinsieme di atleti (recuperi, riatletizzazioni)
                  </span>
                </button>
              </div>
            </div>

            {recipientType === 'team' && (
              <div className={styles.field}>
                <label className={styles.label}>Seleziona squadra</label>
                <select
                  className={styles.input}
                  value={pickedTeamId}
                  onChange={(e) => setPickedTeamId(e.target.value)}
                >
                  <option value="">— Scegli squadra —</option>
                  {(teamsQ.data ?? []).map((t: any) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {recipientType === 'athletes' && (
              <div className={styles.field}>
                <label className={styles.label}>
                  Seleziona atleti ({pickedAthleteIds.length} selezionati)
                </label>
                <div className={styles.athletesList}>
                  {(athletesQ.data ?? []).map((a: any) => {
                    const checked = pickedAthleteIds.includes(a._id);
                    return (
                      <label key={a._id} className={styles.athleteRow}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setPickedAthleteIds((prev) =>
                              e.target.checked
                                ? [...prev, a._id]
                                : prev.filter((id) => id !== a._id),
                            )
                          }
                        />
                        <span>{a.lastName} {a.firstName}</span>
                      </label>
                    );
                  })}
                  {(athletesQ.data ?? []).length === 0 && athletesQ.isFetched && (
                    <div className={styles.empty}>Nessun atleta trovato.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className={styles.bodyInner}>
            <div className={styles.recipientBadge}>
              {recipient?.type === 'team' ? (
                <>
                  <strong>Squadra:</strong>{' '}
                  {(teamsQ.data ?? []).find((t: any) => t._id === recipient.teamId)?.name ?? recipient.teamId}
                </>
              ) : (
                <>
                  <strong>Atleti:</strong>{' '}
                  {recipient?.athleteIds.length} selezionati
                </>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Titolo *</label>
              <input
                className={styles.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Es. Allenamento settimanale"
                autoFocus
              />
            </div>

            <div className={styles.row}>
              <div className={`${styles.field} ${styles.dateField}`}>
                <label className={styles.label}>Data</label>
                <CustomDatePicker
                  date={toDate(date)}
                  onChange={(d) => setDate(toIsoDate(d))}
                  placeholder="gg/mm/aaaa"
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                />
              </div>
              <div className={`${styles.field} ${styles.dateField}`}>
                <label className={styles.label}>Ora inizio</label>
                <CustomDatePicker
                  date={toTimeDate(startTime)}
                  onChange={(d) => setStartTime(toHhmm(d))}
                  placeholder="HH:MM"
                  dateFormat="HH:mm"
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Ora"
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Obiettivo</label>
              <div className={styles.objRow}>
                {OBJECTIVES.map((obj) => (
                  <button
                    key={obj}
                    type="button"
                    className={`${styles.objBtn} ${objective === obj ? styles.objBtnActive : ''}`}
                    onClick={() => setObjective(obj)}
                  >
                    {OBJECTIVE_LABELS[obj]}
                  </button>
                ))}
              </div>
            </div>

            {sport && getSportCapabilities(sport).hasGoalkeepers && (
              <div className={styles.field}>
                <label
                  className={styles.keepersToggle}
                  onClick={() => setKeepersWorkSeparate((v) => !v)}
                >
                  <input
                    type="checkbox"
                    checked={keepersWorkSeparate}
                    onChange={() => setKeepersWorkSeparate((v) => !v)}
                  />
                  <span>Lavoro portieri separato</span>
                  <span className={styles.recipientHint}>
                    Portieri in track parallelo dedicato
                  </span>
                </label>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label}>Parti da</label>
              <div className={styles.objRow}>
                {(['empty', 'leopart', 'personal'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`${styles.objBtn} ${templateSource === s ? styles.objBtnActive : ''}`}
                    onClick={() => {
                      setTemplateSource(s);
                      setSelectedTemplateId('');
                    }}
                  >
                    {s === 'empty'
                      ? 'Vuoto'
                      : s === 'leopart'
                        ? 'Template LEOPART'
                        : 'Template personali'}
                  </button>
                ))}
              </div>
              {templateSource !== 'empty' && templates.length > 0 && (
                <select
                  className={styles.input}
                  style={{ marginTop: 6 }}
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  <option value="">— Seleziona template —</option>
                  {templates.map((t: any) => (
                    <option key={t._id} value={t._id}>
                      {t.name} ({t.structure?.length ?? 0}gg)
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}
      </div>
    </GenericModalV2>
  );
};

export default NewFieldSessionModal;
